/**
 * 小运计算引擎（多语言版）
 * 支持 zh-CN / zh-HK / en / ja / ko
 */

import {
  STEMS, BRANCHES,
  type Stem, type Branch, type FourPillars,
  STEM_ELEMENT,
} from '../../baziCalc';
import { getShishen, ELEMENT_COLORS, type Shishen } from './shishenEngine';

export interface XiaoyunItem {
  age: number;
  year: number;
  stem: Stem;
  branch: Branch;
  stemShishen: Shishen;
  branchMainShishen: Shishen;
  stemColor: string;
  branchColor: string;
  isActive: boolean;
  desc: string;
}

function getBranchMainStem(branch: Branch): Stem {
  const map: Record<Branch, Stem> = {
    子: '癸', 丑: '己', 寅: '甲', 卯: '乙', 辰: '戊', 巳: '丙',
    午: '丁', 未: '己', 申: '庚', 酉: '辛', 戌: '戊', 亥: '壬',
  };
  return map[branch] ?? '甲';
}

// ── 多语言十神名称 ───────────────────────────────────────────
type Lang = string;

const SHISHEN_I18N: Record<Shishen, Record<Lang, string>> = {
  '比肩': { 'zh-CN': '比肩', 'zh-HK': '比肩', 'en': 'Peer',        'ja': '比肩', 'ko': '비견' },
  '劫财': { 'zh-CN': '劫财', 'zh-HK': '劫財', 'en': 'Competitor',  'ja': '劫財', 'ko': '겁재' },
  '食神': { 'zh-CN': '食神', 'zh-HK': '食神', 'en': 'Talent',      'ja': '食神', 'ko': '식신' },
  '伤官': { 'zh-CN': '伤官', 'zh-HK': '傷官', 'en': 'Hurt Off.',   'ja': '傷官', 'ko': '상관' },
  '偏财': { 'zh-CN': '偏财', 'zh-HK': '偏財', 'en': 'Side Wealth', 'ja': '偏財', 'ko': '편재' },
  '正财': { 'zh-CN': '正财', 'zh-HK': '正財', 'en': 'Main Wealth', 'ja': '正財', 'ko': '정재' },
  '七杀': { 'zh-CN': '七杀', 'zh-HK': '七殺', 'en': '7-Killings',  'ja': '七殺', 'ko': '칠살' },
  '正官': { 'zh-CN': '正官', 'zh-HK': '正官', 'en': 'Officer',     'ja': '正官', 'ko': '정관' },
  '偏印': { 'zh-CN': '偏印', 'zh-HK': '偏印', 'en': 'Resource',    'ja': '偏印', 'ko': '편인' },
  '正印': { 'zh-CN': '正印', 'zh-HK': '正印', 'en': 'Seal',        'ja': '正印', 'ko': '정인' },
  '日主': { 'zh-CN': '日主', 'zh-HK': '日主', 'en': 'Day Master',  'ja': '日主', 'ko': '일주' },
};

function getShishenLabel(ss: Shishen, lang: Lang): string {
  return SHISHEN_I18N[ss]?.[lang] ?? SHISHEN_I18N[ss]?.['zh-CN'] ?? ss;
}

// ── 多语言描述构建 ───────────────────────────────────────────
type DescEntry = { pos: string; neg: string };

function buildXiaoyunDesc(
  stem: Stem,
  branch: Branch,
  dayStem: Stem,
  age: number,
  lang: Lang = 'zh-CN',
): string {
  const stemSS = getShishen(dayStem, stem);
  const positive = new Set<Shishen>(['正官', '正印', '食神', '正财', '偏财']);
  const isPositive = positive.has(stemSS);
  const ssLabel = getShishenLabel(stemSS, lang);

  const ageSuffix = lang === 'ja' ? '歳' : lang === 'ko' ? '세' : lang === 'en' ? '' : '岁';
  const agePrefix = lang === 'en' ? `Age ${age}` : `${age}${ageSuffix}`;

  const descMap: Partial<Record<Shishen, Record<Lang, DescEntry>>> = {
    正官: {
      'zh-CN': {
        pos: `${agePrefix}小运，正官护运，社会认可度高，学业或工作中有表现机会，宜积极进取。`,
        neg: `${agePrefix}小运，正官入运，规则意识增强，宜守规矩、踏实行事，避免与权威冲突。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，正官護運，社會認可度高，學業或工作中有表現機會，宜積極進取。`,
        neg: `${agePrefix}小運，正官入運，規則意識增強，宜守規矩、踏實行事，避免與權威衝突。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Officer energy brings social recognition and opportunities to shine in study or work. Stay proactive.`,
        neg: `${agePrefix} minor cycle: Officer energy strengthens rule-consciousness. Act with integrity and avoid conflicts with authority.`,
      },
      'ja': {
        pos: `${agePrefix}小運：正官の運気で社会的認知度が高まり、学業や仕事で活躍の機会があります。積極的に進みましょう。`,
        neg: `${agePrefix}小運：正官の運気でルール意識が高まります。規則を守り、着実に行動し、権威との衝突を避けましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 정관 운기로 사회적 인정도가 높아지고 학업이나 업무에서 활약 기회가 있습니다. 적극적으로 나아가세요.`,
        neg: `${agePrefix} 소운: 정관 운기로 규칙 의식이 강해집니다. 규칙을 지키고 착실하게 행동하며 권위와의 충돌을 피하세요.`,
      },
    },
    七杀: {
      'zh-CN': {
        pos: `${agePrefix}小运，七杀入运，竞争意识强，有压力也有动力，以行动力突破困境。`,
        neg: `${agePrefix}小运，七杀临运，外部压力较大，保持冷静，以实力应对挑战。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，七殺入運，競爭意識強，有壓力也有動力，以行動力突破困境。`,
        neg: `${agePrefix}小運，七殺臨運，外部壓力較大，保持冷靜，以實力應對挑戰。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: 7-Killings energy brings strong competitive drive. Pressure fuels motivation — break through obstacles with decisive action.`,
        neg: `${agePrefix} minor cycle: 7-Killings energy brings external pressure. Stay calm and meet challenges with genuine capability.`,
      },
      'ja': {
        pos: `${agePrefix}小運：七殺の運気で競争意識が強まります。プレッシャーが動力となり、行動力で困難を突破しましょう。`,
        neg: `${agePrefix}小運：七殺の運気で外部からのプレッシャーが大きくなります。冷静を保ち、実力で挑戦に対応しましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 칠살 운기로 경쟁 의식이 강해집니다. 압박이 동력이 되어 행동력으로 어려움을 돌파하세요.`,
        neg: `${agePrefix} 소운: 칠살 운기로 외부 압박이 커집니다. 냉정을 유지하고 실력으로 도전에 대응하세요.`,
      },
    },
    正印: {
      'zh-CN': {
        pos: `${agePrefix}小运，正印护佑，贵人相助，学习进步，精神状态良好，宜充电蓄力。`,
        neg: `${agePrefix}小运，正印流运，节奏偏慢，宜沉淀积累，不宜激进扩张。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，正印護佑，貴人相助，學習進步，精神狀態良好，宜充電蓄力。`,
        neg: `${agePrefix}小運，正印流運，節奏偏慢，宜沉澱積累，不宜激進擴張。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Seal energy brings noble helpers and academic progress. Mental state is excellent — a great time to recharge and build strength.`,
        neg: `${agePrefix} minor cycle: Seal energy slows the pace. Focus on accumulation and deep learning rather than aggressive expansion.`,
      },
      'ja': {
        pos: `${agePrefix}小運：正印の加護で貴人の助けがあり、学習が進歩し精神状態が良好です。充電・蓄力に適した時期です。`,
        neg: `${agePrefix}小運：正印の運気でペースが遅くなります。積み重ねと蓄積に専念し、積極的な拡張は控えましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 정인 운기로 귀인의 도움이 있고 학습이 진보하며 정신 상태가 양호합니다. 충전하고 힘을 비축하기 좋은 시기입니다.`,
        neg: `${agePrefix} 소운: 정인 운기로 속도가 느려집니다. 축적과 심화 학습에 집중하고 공격적인 확장은 자제하세요.`,
      },
    },
    偏印: {
      'zh-CN': {
        pos: `${agePrefix}小运，偏印入运，思维独特，适合深耕专业，研究型工作表现出色。`,
        neg: `${agePrefix}小运，偏印流运，思路多但落地难，需聚焦具体方向前进。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，偏印入運，思維獨特，適合深耕專業，研究型工作表現出色。`,
        neg: `${agePrefix}小運，偏印流運，思路多但落地難，需聚焦具體方向前進。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Resource energy brings unique thinking. Ideal for deep specialization — research-oriented work shines.`,
        neg: `${agePrefix} minor cycle: Resource energy generates many ideas but makes execution difficult. Focus on a specific direction to move forward.`,
      },
      'ja': {
        pos: `${agePrefix}小運：偏印の運気でユニークな思考が生まれます。専門分野の深耕に適しており、研究型の仕事で優れた成果を発揮します。`,
        neg: `${agePrefix}小運：偏印の運気でアイデアは多いですが実現が難しくなります。具体的な方向に集中して前進しましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 편인 운기로 독특한 사고가 생깁니다. 전문 분야 심화에 적합하며 연구형 업무에서 뛰어난 성과를 발휘합니다.`,
        neg: `${agePrefix} 소운: 편인 운기로 아이디어는 많지만 실현이 어렵습니다. 구체적인 방향에 집중하여 전진하세요.`,
      },
    },
    食神: {
      'zh-CN': {
        pos: `${agePrefix}小运，食神流运，才华施展，生活顺遂，创意与灵感涌现，是展示自我的好时机。`,
        neg: `${agePrefix}小运，食神入运，总体顺遂，注意不要因享乐影响效率。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，食神流運，才華施展，生活順遂，創意與靈感湧現，是展示自我的好時機。`,
        neg: `${agePrefix}小運，食神入運，總體順遂，注意不要因享樂影響效率。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Talent energy lets your gifts shine. Life flows smoothly with creativity and inspiration — a great time to express yourself.`,
        neg: `${agePrefix} minor cycle: Talent energy brings overall smoothness. Be mindful not to let leisure affect your productivity.`,
      },
      'ja': {
        pos: `${agePrefix}小運：食神の運気で才能が発揮され、生活が順調で創意とインスピレーションが湧き出ます。自己表現の絶好の機会です。`,
        neg: `${agePrefix}小運：食神の運気で全体的に順調です。享楽が効率に影響しないよう注意しましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 식신 운기로 재능이 발휘되고 생활이 순조로우며 창의와 영감이 솟아납니다. 자기 표현의 좋은 기회입니다.`,
        neg: `${agePrefix} 소운: 식신 운기로 전반적으로 순조롭습니다. 향락이 효율에 영향을 미치지 않도록 주의하세요.`,
      },
    },
    伤官: {
      'zh-CN': {
        pos: `${agePrefix}小运，伤官入运，思维突破，适合创新，才华锋芒毕露。`,
        neg: `${agePrefix}小运，伤官临运，容易与规则产生摩擦，调整沟通方式，将才华引导为正向创造力。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，傷官入運，思維突破，適合創新，才華鋒芒畢露。`,
        neg: `${agePrefix}小運，傷官臨運，容易與規則產生摩擦，調整溝通方式，將才華引導為正向創造力。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Hurt Officer energy breaks mental barriers — ideal for innovation and letting your talents shine brilliantly.`,
        neg: `${agePrefix} minor cycle: Hurt Officer energy may create friction with rules. Adjust your communication style and channel your talents into positive creativity.`,
      },
      'ja': {
        pos: `${agePrefix}小運：傷官の運気で思考が突破し、革新に適しており、才能が輝きます。`,
        neg: `${agePrefix}小運：傷官の運気でルールとの摩擦が生じやすくなります。コミュニケーション方法を調整し、才能をポジティブな創造力に導きましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 상관 운기로 사고가 돌파되어 혁신에 적합하며 재능이 빛납니다.`,
        neg: `${agePrefix} 소운: 상관 운기로 규칙과 마찰이 생기기 쉽습니다. 소통 방식을 조정하고 재능을 긍정적인 창조력으로 이끄세요.`,
      },
    },
    正财: {
      'zh-CN': {
        pos: `${agePrefix}小运，正财流运，收入稳健，踏实努力最有效果，财运稳步上升。`,
        neg: `${agePrefix}小运，正财入运，整体平稳，专注核心能力提升，财运随积累而增长。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，正財流運，收入穩健，踏實努力最有效果，財運穩步上升。`,
        neg: `${agePrefix}小運，正財入運，整體平穩，專注核心能力提升，財運隨積累而增長。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Main Wealth energy brings steady income. Diligent effort yields the best results — wealth fortune rises steadily.`,
        neg: `${agePrefix} minor cycle: Main Wealth energy keeps things stable overall. Focus on building core skills and wealth will grow with accumulation.`,
      },
      'ja': {
        pos: `${agePrefix}小運：正財の運気で収入が安定します。着実な努力が最も効果的で、財運が着実に上昇します。`,
        neg: `${agePrefix}小運：正財の運気で全体的に安定しています。コアスキルの向上に集中すれば、財運は積み重ねとともに成長します。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 정재 운기로 수입이 안정됩니다. 착실한 노력이 가장 효과적이며 재운이 꾸준히 상승합니다.`,
        neg: `${agePrefix} 소운: 정재 운기로 전반적으로 안정적입니다. 핵심 능력 향상에 집중하면 재운이 축적과 함께 성장합니다.`,
      },
    },
    偏财: {
      'zh-CN': {
        pos: `${agePrefix}小运，偏财流运，机会多元，人脉广阔，动中求财是最好的策略。`,
        neg: `${agePrefix}小运，偏财临运，机会多但需辨别，专注主要目标，机会精选不贪多。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，偏財流運，機會多元，人脈廣闊，動中求財是最好的策略。`,
        neg: `${agePrefix}小運，偏財臨運，機會多但需辨別，專注主要目標，機會精選不貪多。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Side Wealth energy brings diverse opportunities and broad connections. Seeking fortune through action is the best strategy.`,
        neg: `${agePrefix} minor cycle: Side Wealth energy brings many opportunities but requires discernment. Focus on primary goals and select opportunities wisely.`,
      },
      'ja': {
        pos: `${agePrefix}小運：偏財の運気で多様な機会と広い人脈があります。動きながら財を求めることが最善の戦略です。`,
        neg: `${agePrefix}小運：偏財の運気で機会は多いですが見極めが必要です。主要な目標に集中し、機会を厳選しましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 편재 운기로 다양한 기회와 넓은 인맥이 있습니다. 움직이면서 재물을 구하는 것이 최선의 전략입니다.`,
        neg: `${agePrefix} 소운: 편재 운기로 기회는 많지만 분별이 필요합니다. 주요 목표에 집중하고 기회를 신중하게 선택하세요.`,
      },
    },
    比肩: {
      'zh-CN': {
        pos: `${agePrefix}小运，比肩入运，同行合作机会增多，稳步推进已有目标，基础更为扎实。`,
        neg: `${agePrefix}小运，比肩流运，同业竞争加剧，保持差异化，展示独特价值。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，比肩入運，同行合作機會增多，穩步推進已有目標，基礎更為扎實。`,
        neg: `${agePrefix}小運，比肩流運，同業競爭加劇，保持差異化，展示獨特價值。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Peer energy increases collaboration opportunities. Steadily advance existing goals — your foundation grows stronger.`,
        neg: `${agePrefix} minor cycle: Peer energy intensifies industry competition. Maintain differentiation and showcase your unique value.`,
      },
      'ja': {
        pos: `${agePrefix}小運：比肩の運気で同業との協力機会が増えます。既存の目標を着実に推進し、基盤がより強固になります。`,
        neg: `${agePrefix}小運：比肩の運気で同業競争が激化します。差別化を保ち、独自の価値を示しましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 비견 운기로 동업 협력 기회가 늘어납니다. 기존 목표를 착실히 추진하면 기반이 더욱 탄탄해집니다.`,
        neg: `${agePrefix} 소운: 비견 운기로 동업 경쟁이 심화됩니다. 차별화를 유지하고 독특한 가치를 보여주세요.`,
      },
    },
    劫财: {
      'zh-CN': {
        pos: `${agePrefix}小运，劫财流运，魄力十足，适合开拓新局面，但需注意合作关系边界。`,
        neg: `${agePrefix}小运，劫财临运，财务方面需谨慎，避免冲动决策，守好自身边界。`,
      },
      'zh-HK': {
        pos: `${agePrefix}小運，劫財流運，魄力十足，適合開拓新局面，但需注意合作關係邊界。`,
        neg: `${agePrefix}小運，劫財臨運，財務方面需謹慎，避免衝動決策，守好自身邊界。`,
      },
      'en': {
        pos: `${agePrefix} minor cycle: Competitor energy brings boldness — great for breaking new ground, but be mindful of boundaries in partnerships.`,
        neg: `${agePrefix} minor cycle: Competitor energy calls for financial caution. Avoid impulsive decisions and protect your personal boundaries.`,
      },
      'ja': {
        pos: `${agePrefix}小運：劫財の運気で魄力が十分にあり、新しい局面を開拓するのに適していますが、協力関係の境界に注意が必要です。`,
        neg: `${agePrefix}小運：劫財の運気で財務面での慎重さが必要です。衝動的な決断を避け、自身の境界を守りましょう。`,
      },
      'ko': {
        pos: `${agePrefix} 소운: 겁재 운기로 기백이 충분하여 새로운 국면을 개척하기에 적합하지만 협력 관계의 경계에 주의해야 합니다.`,
        neg: `${agePrefix} 소운: 겁재 운기로 재무 면에서 신중함이 필요합니다. 충동적인 결정을 피하고 자신의 경계를 지키세요.`,
      },
    },
  };

  const langKey = lang === 'zh-HK' ? 'zh-HK' : lang === 'ja' ? 'ja' : lang === 'ko' ? 'ko' : lang === 'en' ? 'en' : 'zh-CN';
  const entry = descMap[stemSS]?.[langKey];
  if (entry) {
    return isPositive ? entry.pos : entry.neg;
  }

  // fallback
  const fallbacks: Record<Lang, string> = {
    'zh-CN': `${agePrefix}小运，${ssLabel}运程，稳步推进，积累实力，蓄势以待。`,
    'zh-HK': `${agePrefix}小運，${ssLabel}運程，穩步推進，積累實力，蓄勢以待。`,
    'en':    `${agePrefix} minor cycle: ${ssLabel} energy — advance steadily, build strength, and await the right moment.`,
    'ja':    `${agePrefix}小運：${ssLabel}の運程、着実に前進し、実力を積み重ね、好機を待ちましょう。`,
    'ko':    `${agePrefix} 소운: ${ssLabel} 운정, 착실히 전진하고 실력을 쌓으며 좋은 기회를 기다리세요.`,
  };
  return fallbacks[langKey] ?? fallbacks['zh-CN'];
}

/**
 * 计算小运序列（多语言版）
 * 小运：从出生后第1岁开始，到大运起运年龄（startAge）结束，每年一步
 * 方向与大运一致（isForward）
 * 
 * 【重要】小运第1岁（i=0）的干支 = 月柱后第1步（方向与大运相同）
 *         这与大运第1柱的干支完全一致，两者起点相同
 * 
 * 年份对应关系：
 *   第1岁 → 出生年（birthYear + 0）
 *   第2岁 → 出生年+1（birthYear + 1）
 *   第n岁 → birthYear + (n-1)
 */
export function calcXiaoyun(
  fourPillars: FourPillars,
  birthYear: number,
  startAge: number,      // 大运起运年龄（小运的终止年龄）
  isForward: boolean,
  currentYear: number,
  count: number = 20,    // 保留参数兼容性，实际以 startAge 为准
  lang: Lang = 'zh-CN',
): XiaoyunItem[] {
  const { month: monthPillar, day: dayPillar } = fourPillars;
  const dayStem = dayPillar.stem;
  // 小运方向与大运方向完全一致
  const direction = isForward ? 1 : -1;

  const stemIdx = STEMS.indexOf(monthPillar.stem);
  const branchIdx = BRANCHES.indexOf(monthPillar.branch);

  // 小运从1岁开始，到起运年龄（startAge）结束
  // 如果起运年龄 <= 1，则没有小运
  const actualCount = Math.max(0, startAge - 1);
  const items: XiaoyunItem[] = [];

  for (let i = 0; i < actualCount; i++) {
    const age = i + 1;  // 1岁、2岁、3岁……
    // 第1岁对应出生年，第2岁对应出生年+1，以此类推
    const year = birthYear + i;

    // 第1岁(i=0)走月柱后第1步，与大运第1柱干支相同
    const si = ((stemIdx + (i + 1) * direction) % 10 + 10) % 10;
    const bi = ((branchIdx + (i + 1) * direction) % 12 + 12) % 12;
    const stem = STEMS[si];
    const branch = BRANCHES[bi];

    const stemShishen = getShishen(dayStem, stem);
    const branchMainStem = getBranchMainStem(branch);
    const branchMainShishen = getShishen(dayStem, branchMainStem);
    const isActive = year === currentYear;

    items.push({
      age,
      year,
      stem,
      branch,
      stemShishen,
      branchMainShishen,
      stemColor: ELEMENT_COLORS[STEM_ELEMENT[stem]],
      branchColor: ELEMENT_COLORS[STEM_ELEMENT[branchMainStem]],
      isActive,
      desc: buildXiaoyunDesc(stem, branch, dayStem, age, lang),
    });
  }

  return items;
}

export function getCurrentXiaoyun(
  fourPillars: FourPillars,
  birthYear: number,
  startAge: number,
  isForward: boolean,
  currentYear: number,
  lang: Lang = 'zh-CN',
): XiaoyunItem | undefined {
  const items = calcXiaoyun(fourPillars, birthYear, startAge, isForward, currentYear, 30, lang);
  return items.find(it => it.isActive);
}
